import {
  HttpRequest,
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, finalize, map, switchMap, throwError } from 'rxjs';

import { ApiError } from '@errors/error';
import { convertToCamelCase, convertToSnakeCase } from '@utils/string.util';
import { AuthService } from './auth.service';
import { inject } from '@angular/core';
import { LoadingService } from './loading.service';
import { SystemAdminService } from './system-admin.service';

// 認証ヘッダーを除外するURLのSet
const EXCLUDE_AUTHORIZATION_URL_SET: ReadonlySet<string> = new Set([
  '/api/auth/login',
]);

/**
 * API Interceptor
 * @param request リクエスト
 * @param next HTTPハンドラ
 * @returns HTTPハンドラの処理結果Observable
 */
export const apiInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const loadingService = inject(LoadingService);
  const authService = inject(AuthService);
  const systemAdminService = inject(SystemAdminService);

  let headers = request.headers;

  // ローディング制御
  const loadingEnabled = headers.get('x-matp-skip-loading') !== 'true';
  headers = headers.delete('x-matp-skip-loading');
  if (loadingEnabled) {
    loadingService.show();
  }

  // 認証サービスから認証トークンを即時取得
  // 現在時点のトークンが欲しいのでここではObservableで取得しない
  return authService.getAccessToken().pipe(
    switchMap((token) => {
      // ヘッダーの修正
      // リクエストURLがEXCLUDE_AUTHORIZATION_URL_SET以外のデータはAuthorizationヘッダーにトークンを追加する
      const url = new URL(request.url, window.location.origin);
      if (!EXCLUDE_AUTHORIZATION_URL_SET.has(url.pathname)) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }

      // システム管理者の場合、テナントIDをヘッダーに追加
      const loggedInUser = authService.loggedInUser;
      const roleId = loggedInUser?.tenant.roleId;
      if (
        roleId === 'system_admin' &&
        systemAdminService.isSelectedTenant &&
        !systemAdminService.isSelectedSystemAdminTenant
      ) {
        const tenantId = systemAdminService.getTenantId();
        if (tenantId !== null) {
          headers = headers.set('x-matp-tenant', tenantId);
        }
      }

      // リクエストボディをスネークケースに変換する
      const convertedBody = convertToSnakeCase(request.body);
      const modifiedReq = request.clone({ headers, body: convertedBody });

      return next(modifiedReq).pipe(
        map((response) => {
          if (
            response instanceof HttpResponse &&
            response.headers.get('content-type')?.includes('application/json')
          ) {
            // レスポンスボディをキャメルケースに変換する
            const convertedBody = convertToCamelCase(response.body);
            return response.clone({
              body: convertedBody,
            });
          }
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new ApiError(request, error));
        }),
      );
    }),
    finalize(() => {
      if (loadingEnabled) {
        loadingService.hide();
      }
    }),
  );
};
